// Wraps the `straight-skeleton` (CGAL via WASM) library and converts its
// output into our Shape format used by the animator.
//
// CGAL output:
//   vertices: [x, y, t][]    -- contour vertices have t=0
//   polygons: number[][]     -- each face = vertex-index list around one
//                                "roof slope" above an original polygon edge.
//                                The wraparound edge (last → first) is the
//                                original polygon edge; the rest is the
//                                skeleton chain bordering this face.
//
// Animation derivation:
//   - traces: each unique skeleton edge becomes a line drawn over [tMin, tMax]
//   - ridges: skeleton edges where both endpoints share a time = "born at t"
//   - wavefronts (per phase between consecutive event times): for each phase,
//     intersect every face's skeleton chain with the level line "t = t_rep",
//     pair the crossings within each face into segments, and stitch segments
//     across adjacent faces (which share a skeleton edge crossing) into closed
//     wavefront polygon(s). Each wavefront polygon vertex sits on one skeleton
//     edge and moves linearly along it within the phase.

import * as ss from 'straight-skeleton';

export type Pt = [number, number];

type Trace = {
  from: Pt;
  to: Pt;
  tStart: number;
  tEnd: number;
  reflex?: boolean;
};
type Ridge = {
  from: Pt;
  to: Pt;
  tBorn: number;
};

type WavefrontVertex = {
  at_tStart: Pt;
  at_tEnd: Pt;
  reflex?: boolean;
};

type Wavefront = {
  tStart: number;
  tEnd: number;
  vertices: WavefrontVertex[];
};

export type Event = {
  t: number;
  label: string;
  pending?: boolean;
};

export type Shape = {
  note: string;
  original: Pt[];
  weights?: number[];
  traces: Trace[];
  ridges: Ridge[];
  wavefronts: Wavefront[];
  events: Event[];
  tMax: number;
  bounds: { x: number; y: number; w: number; h: number };
};

// The package ships UMD/CJS. Depending on how the bundler interops, the API
// can land on the namespace, on .default, or on the window global. Cover all.
const SkeletonBuilder = (() => {
  const ns = ss as unknown as Record<string, unknown>;
  const fromNamespace = ns.SkeletonBuilder as SkeletonBuilderApi | undefined;
  if (fromNamespace) return fromNamespace;
  const def = ns.default as { SkeletonBuilder?: SkeletonBuilderApi } | undefined;
  if (def?.SkeletonBuilder) return def.SkeletonBuilder;
  const onWindow = (globalThis as unknown as { SkeletonBuilder?: SkeletonBuilderApi })
    .SkeletonBuilder;
  if (onWindow) return onWindow;
  throw new Error('SkeletonBuilder not found on the straight-skeleton module');
})();

await SkeletonBuilder.init();

type Vertex = [number, number, number];
type SkeletonResult = { vertices: Vertex[]; polygons: number[][] };

type SkeletonBuilderApi = {
  init: () => Promise<void>;
  buildFromPolygon: (rings: number[][][]) => SkeletonResult | null;
};

// Signed area via shoelace. Positive = CCW, negative = CW.
function signedArea(polygon: Pt[]): number {
  let s = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s / 2;
}

// CGAL needs a CCW outer ring. If the input is CW, reverse it.
function ensureCCW(polygon: Pt[]): Pt[] {
  return signedArea(polygon) < 0 ? polygon.slice().reverse() : polygon;
}

function isReflexVertex(polygon: Pt[], i: number): boolean {
  const n = polygon.length;
  const prev = polygon[(i - 1 + n) % n];
  const cur = polygon[i];
  const next = polygon[(i + 1) % n];
  const ax = cur[0] - prev[0];
  const ay = cur[1] - prev[1];
  const bx = next[0] - cur[0];
  const by = next[1] - cur[1];
  return ax * by - ay * bx < 0; // CCW polygon: cross < 0 means reflex
}

const TIME_EPS = 1e-7;

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

function interpAlongEdge(u: Vertex, v: Vertex, t: number): Pt {
  const tu = u[2];
  const tv = v[2];
  if (Math.abs(tv - tu) < TIME_EPS) return [u[0], u[1]];
  const s = Math.max(0, Math.min(1, (t - tu) / (tv - tu)));
  return [u[0] + s * (v[0] - u[0]), u[1] + s * (v[1] - u[1])];
}

// One wavefront-polygon edge inside a single face: a segment between two
// crossings on the face's skeleton chain.
type FaceSegment = {
  startEdge: string;
  endEdge: string;
};

function computeSegmentsAtTime(skel: SkeletonResult, t: number): FaceSegment[] {
  const segments: FaceSegment[] = [];
  for (const face of skel.polygons) {
    // Skeleton-chain edges are face[i]→face[i+1] for i in 0..n-2.
    // The wraparound face[n-1]→face[0] is the original polygon edge (skipped).
    const crossings: string[] = [];
    for (let i = 0; i < face.length - 1; i++) {
      const a = face[i];
      const b = face[i + 1];
      const va = skel.vertices[a];
      const vb = skel.vertices[b];
      const ta = va[2];
      const tb = vb[2];
      const lo = Math.min(ta, tb);
      const hi = Math.max(ta, tb);
      if (t < lo - TIME_EPS || t > hi + TIME_EPS) continue;
      if (Math.abs(ta - tb) < TIME_EPS) continue; // degenerate constant-time edge
      crossings.push(edgeKey(a, b));
    }
    // Pair consecutive crossings (alternating enter/exit along the chain)
    for (let i = 0; i + 1 < crossings.length; i += 2) {
      segments.push({ startEdge: crossings[i], endEdge: crossings[i + 1] });
    }
  }
  return segments;
}

// Stitch face segments into closed wavefront polygons, returning each polygon
// as an ordered list of skeleton-edge keys (one per wavefront-polygon vertex).
function stitchSegments(segments: FaceSegment[]): string[][] {
  if (segments.length === 0) return [];

  const edgeToSegs = new Map<string, number[]>();
  for (let i = 0; i < segments.length; i++) {
    for (const ek of [segments[i].startEdge, segments[i].endEdge]) {
      const list = edgeToSegs.get(ek) ?? [];
      list.push(i);
      edgeToSegs.set(ek, list);
    }
  }

  const used = new Array<boolean>(segments.length).fill(false);
  const polygons: string[][] = [];

  for (let i = 0; i < segments.length; i++) {
    if (used[i]) continue;
    const polygon: string[] = [];
    let curSeg = i;
    let prevEdge = segments[curSeg].startEdge;
    used[curSeg] = true;
    polygon.push(prevEdge);

    while (true) {
      const seg = segments[curSeg];
      const otherEdge = prevEdge === seg.startEdge ? seg.endEdge : seg.startEdge;
      // Pick the next segment touching otherEdge (in the adjacent face)
      const candidates = edgeToSegs.get(otherEdge) ?? [];
      let nextSeg = -1;
      for (const c of candidates) {
        if (c !== curSeg && !used[c]) {
          nextSeg = c;
          break;
        }
      }
      if (nextSeg === -1) break; // cycle should have closed
      polygon.push(otherEdge);
      used[nextSeg] = true;
      curSeg = nextSeg;
      prevEdge = otherEdge;
    }
    if (polygon.length >= 3) polygons.push(polygon);
  }
  return polygons;
}

function uniqueSorted(arr: number[]): number[] {
  const out: number[] = [];
  for (const v of [...arr].sort((a, b) => a - b)) {
    if (out.length === 0 || Math.abs(out[out.length - 1] - v) > TIME_EPS) out.push(v);
  }
  return out;
}

function buildWavefronts(
  skel: SkeletonResult,
  _polygon: Pt[],
  isReflexAt: (contourIdx: number) => boolean,
): Wavefront[] {
  const verts = skel.vertices;
  const eventTimes = uniqueSorted(verts.filter(v => v[2] > TIME_EPS).map(v => v[2]));
  if (eventTimes.length === 0) return [];

  // Phase boundaries: 0 and every event time
  const boundaries = [0, ...eventTimes];
  const out: Wavefront[] = [];

  for (let p = 0; p + 1 < boundaries.length; p++) {
    const tStart = boundaries[p];
    const tEnd = boundaries[p + 1];
    if (tEnd - tStart < TIME_EPS) continue;
    const tRep = 0.5 * (tStart + tEnd);
    const segments = computeSegmentsAtTime(skel, tRep);
    if (segments.length === 0) continue;
    const polygonsByEdges = stitchSegments(segments);

    for (const edgeKeys of polygonsByEdges) {
      const wfVerts: WavefrontVertex[] = edgeKeys.map(ek => {
        const [aStr, bStr] = ek.split('-');
        const a = Number(aStr);
        const b = Number(bStr);
        const va = verts[a];
        const vb = verts[b];
        const [u, v] = va[2] <= vb[2] ? [va, vb] : [vb, va];
        const reflex = u[2] === 0 ? isReflexAt(va[2] <= vb[2] ? a : b) : false;
        return {
          at_tStart: interpAlongEdge(u, v, tStart),
          at_tEnd: interpAlongEdge(u, v, tEnd),
          reflex,
        };
      });
      out.push({ tStart, tEnd, vertices: wfVerts });
    }
  }
  return out;
}

function computeSkeleton(polygon: Pt[]): SkeletonResult | null {
  if (polygon.length < 3) return null;
  // CGAL wants the first vertex repeated at the end
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  const ring = polygon.map(p => [p[0], p[1]] as [number, number]);
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  return SkeletonBuilder.buildFromPolygon([ring]);
}

export function buildShapeFromPolygon(rawPolygon: Pt[]): Shape | { error: string } {
  const polygon = ensureCCW(rawPolygon);
  const skel = computeSkeleton(polygon);
  if (!skel)
    return { error: 'CGAL returned null (polygon may be degenerate or self-intersecting)' };

  const verts = skel.vertices;
  const polys = skel.polygons;
  const isContour = (i: number): boolean => verts[i][2] === 0;

  // Collect unique skeleton edges across all faces
  const seen = new Set<string>();
  const edges: Array<[number, number]> = [];
  for (const face of polys) {
    for (let i = 0; i < face.length; i++) {
      const a = face[i];
      const b = face[(i + 1) % face.length];
      if (isContour(a) && isContour(b)) continue; // original polygon edge, not skeleton
      const key = a < b ? `${a}-${b}` : `${b}-${a}`;
      if (!seen.has(key)) {
        seen.add(key);
        edges.push([a, b]);
      }
    }
  }

  // Compute reflex flags for the original polygon vertices.
  // A trace whose `from` is at a reflex polygon vertex is itself "reflex" for rendering.
  const reflexByContour = new Map<number, boolean>();
  for (let i = 0; i < polygon.length; i++) {
    reflexByContour.set(i, isReflexVertex(polygon, i));
  }
  const isReflexAt = (idx: number): boolean => reflexByContour.get(idx) === true;

  // Convert edges to traces (different times) or ridges (same time)
  const traces: Trace[] = [];
  const ridges: Ridge[] = [];
  for (const [a, b] of edges) {
    const va = verts[a];
    const vb = verts[b];
    const [uIdx, vIdx] = va[2] <= vb[2] ? [a, b] : [b, a];
    const u = verts[uIdx];
    const v = verts[vIdx];
    if (Math.abs(u[2] - v[2]) < 1e-9) {
      ridges.push({ from: [u[0], u[1]], to: [v[0], v[1]], tBorn: u[2] });
    } else {
      traces.push({
        from: [u[0], u[1]],
        to: [v[0], v[1]],
        tStart: u[2],
        tEnd: v[2],
        reflex: isReflexAt(uIdx),
      });
    }
  }

  // Build per-phase wavefronts by face-segment-and-stitch. Each phase is a
  // time interval between consecutive event times. Within a phase, every
  // wavefront polygon vertex slides linearly along one skeleton edge.
  const wavefronts = buildWavefronts(skel, polygon, isReflexAt);

  // Events: list interior vertices in time order
  const interior = verts
    .map((v, i) => ({ idx: i, x: v[0], y: v[1], t: v[2] }))
    .filter(v => v.t > 0)
    .sort((a, b) => a.t - b.t);
  const events: Event[] = interior.map(v => ({
    t: v.t,
    label: `skeleton vertex at (${v.x.toFixed(2)}, ${v.y.toFixed(2)})`,
  }));

  // Bounds from polygon extent
  const xs = polygon.map(p => p[0]);
  const ys = polygon.map(p => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const tMax = interior.length > 0 ? interior[interior.length - 1].t : 0;

  return {
    note: `${polygon.length}-gon: ${interior.length} interior skeleton vertices, ${wavefronts.length} wavefront phases, tMax=${tMax.toFixed(3)}.`,
    original: polygon,
    traces,
    ridges,
    wavefronts,
    events,
    tMax: tMax || 1,
    bounds: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
  };
}
